//------------------------------------------------------
// Blur metrics for the video modules
//
// Flag 1 (2026-10-26), the same for stencil shots, guidance frames and interim
// frames:
//
//   Vehicle parts   Method                          Filtered when
//   always          Full frame: raw Laplacian       below 630
//   yes             Vehicle crop: blurry cells      12+ of 16 cells blurry
//                   Vehicle crop: norm score        below 125,000
//   no              Full frame: blurry cells        12+ of 16 cells blurry
//                   Full frame: norm score          below 125,000
//
// The raw check runs first and a frame it filters goes no further. Otherwise
// whether vehicle parts were detected picks what the grid and norm checks are
// measured on -- the vehicle crop, the union of the detected parts' boxes, or
// the full frame when there is no reliable crop -- and either of the two
// filters the frame. Thresholds are the same both ways.
//
// The raw Laplacian is the full frame's Laplacian variance with no brightness
// correction. The norm score is the measured area's Laplacian variance over its
// mean brightness squared, with the brightness capped at 100 (raised from 80 on
// 2026-10-26 after reviewing real flip-frames, accepting more false positives
// on bright daylight shots -- see brightness_cap_decision_2026-10-26.txt).
// Validated against 32 flip-frames across 3 real inspection sets.
//
// Preprocessing follows imageQuality's grid -- box-filter downscale to 200px on
// the long side, grayscale, 4x4 cells -- except that pixels count as bright for
// the glare check from 235 rather than 200, and headlights are not excluded.
//------------------------------------------------------

import { boxesFromDetections, clipBBox } from "./imageQuality";

const ROWS = 4;
const COLS = 4;
const CELLS = ROWS * COLS;
const MAX_SIZE = 200;

// Per-cell cuts: blurry when the Laplacian variance is under LAP1, the
// Tenengrad under TEN, or the Laplacian variance under LAP2 in a glared cell.
const LAP1 = 630;
const TEN = 12500;
const LAP2 = 900;
const GLARE_T = 2000;
const BRIGHT = 235;
const BRIGHTNESS_CAP = 100;

export const RAW_LAP_MIN = 630;
export const BLURRY_CELLS = 12;
export const NORM_SCORE_MIN = 125000;

/**
 * Grid figures for `rect`, a window into the full-resolution frame `fullData`
 * (RGBA, `imgWidth` pixels wide), measured as if it had been cropped out first.
 */
const buildGrid = (fullData, imgWidth, rect) => {
  const sw = rect.width;
  const sh = rect.height;
  const sc = Math.min(1, MAX_SIZE / Math.max(sw, sh));
  const w = Math.max(1, Math.round(sw * sc));
  const h = Math.max(1, Math.round(sh * sc));

  // Box-filter downscale to grayscale: each destination pixel is the mean luma
  // of the source pixels it covers.
  const gray = new Uint8Array(w * h);
  const xE = new Int32Array(w + 1);
  for (let d = 0; d <= w; d++) xE[d] = Math.floor((d * sw) / w);
  const yE = new Int32Array(h + 1);
  for (let d = 0; d <= h; d++) yE[d] = Math.floor((d * sh) / h);
  let bSum = 0;
  for (let dy = 0; dy < h; dy++) {
    const s0 = yE[dy];
    const s1 = Math.min(sh, Math.max(s0 + 1, yE[dy + 1]));
    for (let dx = 0; dx < w; dx++) {
      const x0 = xE[dx];
      const x1 = Math.min(sw, Math.max(x0 + 1, xE[dx + 1]));
      let sum = 0;
      let n = 0;
      for (let sy = s0; sy < s1; sy++) {
        let i = ((rect.y + sy) * imgWidth + rect.x + x0) * 4;
        for (let sx = x0; sx < x1; sx++, i += 4) {
          sum +=
            0.299 * fullData[i] +
            0.587 * fullData[i + 1] +
            0.114 * fullData[i + 2];
          n++;
        }
      }
      gray[dy * w + dx] = sum / n;
      bSum += gray[dy * w + dx];
    }
  }

  const tw = w / COLS;
  const th = h / ROWS;
  const ce = [];
  const re = [];
  for (let k = 0; k <= COLS; k++) ce.push(Math.round(k * tw));
  for (let k = 0; k <= ROWS; k++) re.push(Math.round(k * th));
  const colOf = new Int8Array(w);
  const rowOf = new Int8Array(h);
  for (let k = 0; k < COLS; k++)
    for (let x = ce[k]; x < ce[k + 1]; x++) colOf[x] = k;
  for (let k = 0; k < ROWS; k++)
    for (let y = re[k]; y < re[k + 1]; y++) rowOf[y] = k;

  const ls = new Float64Array(CELLS);
  const lq = new Float64Array(CELLS);
  const lc = new Float64Array(CELLS);
  const ts = new Float64Array(CELLS);
  const bc = new Float64Array(CELLS);
  const vc = new Float64Array(CELLS);
  let gLapSum = 0;
  let gLapSq = 0;
  let gN = 0;

  for (let y = 0; y < h; y++) {
    const rb = rowOf[y] * COLS;
    for (let x = 0; x < w; x++) {
      const c = rb + colOf[x];
      vc[c]++;
      if (gray[y * w + x] > BRIGHT) bc[c]++;
    }
  }

  for (let y = 1; y < h - 1; y++) {
    const rb = rowOf[y] * COLS;
    for (let x = 1; x < w - 1; x++) {
      const p = y * w + x;
      // 4-neighbour Laplacian, and Sobel X/Y for the Tenengrad.
      const lap =
        gray[p - w] + gray[p - 1] + gray[p + 1] + gray[p + w] - 4 * gray[p];
      const gx =
        -gray[p - w - 1] +
        gray[p - w + 1] -
        2 * gray[p - 1] +
        2 * gray[p + 1] -
        gray[p + w - 1] +
        gray[p + w + 1];
      const gy =
        -gray[p - w - 1] -
        2 * gray[p - w] -
        gray[p - w + 1] +
        gray[p + w - 1] +
        2 * gray[p + w] +
        gray[p + w + 1];
      gLapSum += lap;
      gLapSq += lap * lap;
      gN++;
      const c = rb + colOf[x];
      ls[c] += lap;
      lq[c] += lap * lap;
      lc[c]++;
      ts[c] += gx * gx + gy * gy;
    }
  }

  // Binary variance of the bright-pixel share: high only when a cell is partly
  // blown out, which is what eats the gradients focus is measured from.
  const bv = (b, v) => {
    const p = v > 0 ? b / v : 0;
    return p * (1 - p) * 255 * 255;
  };
  let blurryCells = 0;
  for (let i = 0; i < CELLS; i++) {
    const n = lc[i];
    if (!(n > 0)) continue;
    const mu = ls[i] / n;
    const lapVar = lq[i] / n - mu * mu;
    const ten = ts[i] / n;
    const hasGlare = bv(bc[i], vc[i]) > GLARE_T;
    if (lapVar < LAP1 || ten < TEN || (lapVar < LAP2 && hasGlare))
      blurryCells++;
  }

  const wholeLapVar = gN ? gLapSq / gN - Math.pow(gLapSum / gN, 2) : 0;
  const meanBrightness = w * h ? bSum / (w * h) : 0;
  return { blurryCells, wholeLapVar, meanBrightness };
};

const loadImageData = (base64) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);
        resolve({
          data: ctx.getImageData(0, 0, img.width, img.height).data,
          width: img.width,
          height: img.height,
        });
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error("Could not load image."));
    img.src = base64.startsWith("data:")
      ? base64
      : `data:image/jpeg;base64,${base64}`;
  });

/**
 * Judge one frame by the routing above. `detections` are the frame's part
 * detections, with bboxes normalised to 0-1 as both part detectors give them.
 *
 * Resolves { blurred, vehiclePartsDetected, rawLapVar, filteredByRaw,
 * blurryCells, cellsNeeded, normScore, filteredByCells, filteredByNorm,
 * wholeLapVar, meanBrightness }. When the raw check filters the frame the
 * routed checks do not run: blurryCells and normScore are null, and wholeLapVar
 * and meanBrightness are the full frame's.
 */
export const evaluateBlurFrame = async (base64, detections) => {
  const { data, width, height } = await loadImageData(base64);
  const boxes = boxesFromDetections(detections, width, height);
  const full = buildGrid(data, width, { x: 0, y: 0, width, height });

  if (full.wholeLapVar < RAW_LAP_MIN) {
    return {
      blurred: true,
      vehiclePartsDetected: boxes.hasParts,
      rawLapVar: full.wholeLapVar,
      filteredByRaw: true,
      blurryCells: null,
      cellsNeeded: BLURRY_CELLS,
      normScore: null,
      filteredByCells: false,
      filteredByNorm: false,
      wholeLapVar: full.wholeLapVar,
      meanBrightness: full.meanBrightness,
    };
  }

  // Without parts `car` is the full frame, whose grid is already in hand.
  const grid = boxes.hasParts
    ? buildGrid(data, width, clipBBox(boxes.car, width, height))
    : full;
  const cappedBrightness = Math.min(grid.meanBrightness, BRIGHTNESS_CAP);
  const normScore =
    cappedBrightness > 0
      ? (grid.wholeLapVar / (cappedBrightness * cappedBrightness)) * 1e6
      : 0;
  const filteredByCells = grid.blurryCells >= BLURRY_CELLS;
  const filteredByNorm = normScore < NORM_SCORE_MIN;
  return {
    blurred: filteredByCells || filteredByNorm,
    vehiclePartsDetected: boxes.hasParts,
    rawLapVar: full.wholeLapVar,
    filteredByRaw: false,
    blurryCells: grid.blurryCells,
    cellsNeeded: BLURRY_CELLS,
    normScore,
    filteredByCells,
    filteredByNorm,
    wholeLapVar: grid.wholeLapVar,
    meanBrightness: grid.meanBrightness,
  };
};
